import axiosInstance from './apiinterseptor';
import { toast } from 'react-hot-toast';

export interface IAward {
  _id: string;
  name: string;
  category: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface INomination {
  _id: string;
  userId: string;
  awardId: string | IAward;
  createdAt?: string;
  updatedAt?: string;
}

export interface GetAwardsResponse {
  status: string;
  message: string;
  data: IAward[];
}

export interface GetNominationsResponse {
  status: string;
  message: string;
  data: INomination[];
}

export interface ITeamNomination {
  nominationId: string;
  userId: string;
  username: string;
  userEmail: string;
  userPhone?: string;
  userRole: string;
  awardId: string;
  awardName: string;
  awardCategory: string;
  awardDescription?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TeamNominationsParams {
  page?: number;
  limit?: number;
  search?: string;
  awardId?: string;
}

export interface GetTeamNominationsResponse {
  status: string;
  message: string;
  data: ITeamNomination[];
  metadata: {
    totalResults: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * Get all awards (with optional category filter)
 */
export const getAllAwards = async (category?: string): Promise<IAward[]> => {
  try {
    const queryParams = new URLSearchParams();
    if (category) {
      queryParams.append('category', category);
    }

    const url = category ? `/awards?${queryParams.toString()}` : '/awards';
    const response = await axiosInstance.get<GetAwardsResponse>(url);
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch awards:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch awards. Please try again.');
    throw error;
  }
};

/**
 * Get award by ID
 */
export const getAwardById = async (awardId: string): Promise<IAward> => {
  try {
    const response = await axiosInstance.get<{ status: string; message: string; data: IAward }>(`/awards/${awardId}`);
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch award:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch award.');
    throw error;
  }
};

/**
 * Create a nomination
 */
export const createNomination = async (awardId: string): Promise<INomination> => {
  try {
    const response = await axiosInstance.post<{ status: string; message: string; data: INomination }>('/nominations', {
      awardId,
    });
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to create nomination:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to submit nomination. Please try again.');
    throw error;
  }
};

/**
 * Get current user's nominations
 */
export const getMyNominations = async (): Promise<INomination[]> => {
  try {
    const response = await axiosInstance.get<GetNominationsResponse>('/nominations/my-nominations');
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch nominations:', error.response?.data?.message || error.message);
    // Don't show toast for this as it's called on page load
    throw error;
  }
};

/**
 * Delete a nomination
 */
export const deleteNomination = async (nominationId: string): Promise<void> => {
  try {
    await axiosInstance.delete(`/nominations/${nominationId}`);
  } catch (error: any) {
    console.error('Failed to delete nomination:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to remove nomination. Please try again.');
    throw error;
  }
};

/**
 * Get user's nomination count
 */
export const getUserNominationCount = async (): Promise<number> => {
  try {
    const response = await axiosInstance.get<{ status: string; message: string; data: { count: number } }>('/nominations/my-count');
    return response.data.data.count;
  } catch (error: any) {
    console.error('Failed to fetch nomination count:', error.response?.data?.message || error.message);
    return 0;
  }
};

/**
 * Submit nominations (batch create/delete)
 */
export const submitNominations = async (
  awardIdsToCreate: string[],
  awardIdsToDelete: string[]
): Promise<{ created: number; deleted: number; nominations: INomination[] }> => {
  try {
    const response = await axiosInstance.post<{
      status: string;
      message: string;
      data: { created: number; deleted: number; nominations: INomination[] };
    }>('/nominations/submit', {
      awardIdsToCreate,
      awardIdsToDelete,
    });
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to submit nominations:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to submit nominations. Please try again.');
    throw error;
  }
};

/**
 * Send app promotion WhatsApp messages
 */
export interface SendWhatsAppResponse {
  success: number;
  failed: number;
  total: number;
  results: Array<{
    phone: string;
    status: string;
    messageId?: string;
  }>;
  errors: Array<{
    phone: string;
    status: string;
    error: string;
  }>;
}

export const sendAppPromotionWhatsApp = async (
  phoneNumbers: string[],
  appLink: string
): Promise<SendWhatsAppResponse> => {
  try {
    const response = await axiosInstance.post<{
      status: string;
      message: string;
      data: SendWhatsAppResponse;
    }>('/nominations/send-whatsapp', {
      phoneNumbers,
      appLink,
    });
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to send WhatsApp messages:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to send WhatsApp messages. Please try again.');
    throw error;
  }
};

/**
 * Referral Contact interfaces
 */
export interface IReferralContact {
  _id: string;
  userId: string;
  phoneNumber: string;
  referralName?: string;
  appLink: string;
  sentAt: string;
  accountCreated: boolean;
  status: 'sent' | 'delivered' | 'failed';
  messageId?: string;
  errorMessage?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GetReferralContactsResponse {
  status: string;
  message: string;
  data: IReferralContact[];
  metadata: {
    totalResults: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface ReferralStats {
  totalSent: number;
  accountCreated: number;
  successful: number;
  failed: number;
  conversionRate: string;
}

export interface GetReferralStatsResponse {
  status: string;
  message: string;
  data: ReferralStats;
}

/**
 * Get user's referral contacts
 */
export const getMyReferralContacts = async (
  page: number = 1,
  limit: number = 50
): Promise<{ referrals: IReferralContact[]; metadata: GetReferralContactsResponse['metadata'] }> => {
  try {
    const response = await axiosInstance.get<GetReferralContactsResponse>(
      `/nominations/my-referrals?page=${page}&limit=${limit}`
    );
    return {
      referrals: response.data.data,
      metadata: response.data.metadata,
    };
  } catch (error: any) {
    console.error('Failed to fetch referral contacts:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch referral contacts. Please try again.');
    throw error;
  }
};

/**
 * Get user's referral statistics
 */
export const getMyReferralStats = async (): Promise<ReferralStats> => {
  try {
    const response = await axiosInstance.get<GetReferralStatsResponse>('/nominations/my-referral-stats');
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch referral stats:', error.response?.data?.message || error.message);
    // Don't show toast for stats as it's called on page load
    throw error;
  }
};

/**
 * Update referral contact account created status
 */
export const updateReferralContactStatus = async (
  referralContactId: string,
  accountCreated: boolean
): Promise<IReferralContact> => {
  try {
    const response = await axiosInstance.patch<{
      status: string;
      message: string;
      data: IReferralContact;
    }>(`/nominations/referrals/${referralContactId}`, {
      accountCreated,
    });
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to update referral contact status:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to update referral contact status.');
    throw error;
  }
};

/**
 * Get team nominations (hierarchy-based)
 * - ADMIN: can see all nominations
 * - SHM: can see HM and HR nominations
 * - FBDE: can see HM and HR nominations
 * - HM: can see HR nominations
 */
export const getTeamNominations = async (
  params?: TeamNominationsParams
): Promise<{ nominations: ITeamNomination[]; metadata: GetTeamNominationsResponse['metadata'] }> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.awardId) queryParams.append('awardId', params.awardId);

    const url = queryParams.toString() 
      ? `/nominations/team-nominations?${queryParams.toString()}`
      : '/nominations/team-nominations';

    const response = await axiosInstance.get<GetTeamNominationsResponse>(url);
    return {
      nominations: response.data.data,
      metadata: response.data.metadata,
    };
  } catch (error: any) {
    console.error('Failed to fetch team nominations:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch team nominations. Please try again.');
    throw error;
  }
};

/**
 * Interface for populated nomination (when userId and awardId are populated)
 */
export interface IPopulatedNomination extends Omit<INomination, 'userId' | 'awardId'> {
  userId: {
    _id: string;
    username: string;
    email: string;
    phone?: string;
    role?: string;
  };
  awardId: IAward;
}

export interface GetAllNominationsParams {
  page?: number;
  limit?: number;
  userId?: string;
  awardId?: string;
}

export interface GetAllNominationsResponse {
  status: string;
  message: string;
  data: IPopulatedNomination[];
  metadata: {
    totalResults: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * Get all nominations (Admin only)
 */
export const getAllNominationsAdmin = async (
  params?: GetAllNominationsParams
): Promise<{ nominations: IPopulatedNomination[]; metadata: GetAllNominationsResponse['metadata'] }> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.userId) queryParams.append('userId', params.userId);
    if (params?.awardId) queryParams.append('awardId', params.awardId);

    const url = queryParams.toString() 
      ? `/nominations/all?${queryParams.toString()}`
      : '/nominations/all';

    const response = await axiosInstance.get<GetAllNominationsResponse>(url);
    return {
      nominations: response.data.data,
      metadata: response.data.metadata,
    };
  } catch (error: any) {
    console.error('Failed to fetch all nominations:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch all nominations. Please try again.');
    throw error;
  }
};

/**
 * Interface for registered user with nominations
 */
export interface IRegisteredUserWithNominations {
  _id: string;
  username: string;
  email: string;
  phone?: string;
  role: string;
  createdAt: string;
  nominationCount: number;
  nominationNames?: string[];
}

export interface GetRegisteredUsersParams {
  page?: number;
  limit?: number;
  search?: string;
}

export interface GetRegisteredUsersResponse {
  status: string;
  message: string;
  data: {
    users: IRegisteredUserWithNominations[];
    totalResults: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * Get registered users who have made nominations (Admin only)
 */
export const getRegisteredUsersWithNominations = async (
  params?: GetRegisteredUsersParams
): Promise<{ users: IRegisteredUserWithNominations[]; metadata: Omit<GetRegisteredUsersResponse['data'], 'users'> }> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);

    const url = queryParams.toString() 
      ? `/nominations/registered-users?${queryParams.toString()}`
      : '/nominations/registered-users';

    const response = await axiosInstance.get<GetRegisteredUsersResponse>(url);
    const { users, ...metadata } = response.data.data;
    return {
      users,
      metadata,
    };
  } catch (error: any) {
    console.error('Failed to fetch registered users with nominations:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch registered users with nominations. Please try again.');
    throw error;
  }
};

export interface GetUnregisteredUsersParams {
  page?: number;
  limit?: number;
  search?: string;
}

export interface GetUnregisteredUsersResponse {
  status: string;
  message: string;
  data: {
    users: IRegisteredUserWithNominations[];
    totalResults: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * Get unregistered active users who haven't made nominations (Admin only)
 */
export const getUnregisteredActiveUsers = async (
  params?: GetUnregisteredUsersParams
): Promise<{ users: IRegisteredUserWithNominations[]; metadata: Omit<GetUnregisteredUsersResponse['data'], 'users'> }> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);

    const url = queryParams.toString() 
      ? `/nominations/unregistered-users?${queryParams.toString()}`
      : '/nominations/unregistered-users';

    const response = await axiosInstance.get<GetUnregisteredUsersResponse>(url);
    const { users, ...metadata } = response.data.data;
    return {
      users,
      metadata,
    };
  } catch (error: any) {
    console.error('Failed to fetch unregistered active users:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch unregistered active users. Please try again.');
    throw error;
  }
};

/**
 * Interface for highest app downloads result
 */
export interface IHighestAppDownload {
  userId: string;
  username: string;
  email: string;
  role?: string;
  downloadsCount: number;
  totalSent: number;
}

export interface GetHighestAppDownloadsResponse {
  status: string;
  message: string;
  data: IHighestAppDownload[];
}

/**
 * Get highest app downloads (Admin only)
 */
export const getHighestAppDownloads = async (
  limit: number = 10
): Promise<IHighestAppDownload[]> => {
  try {
    const response = await axiosInstance.get<GetHighestAppDownloadsResponse>(
      `/nominations/highest-app-downloads?limit=${limit}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch highest app downloads:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch highest app downloads. Please try again.');
    throw error;
  }
};

/**
 * Interface for highest app downloads by team result
 */
export interface IHighestAppDownloadsByTeam {
  teamLeaderId: string;
  teamLeaderUsername: string;
  teamLeaderEmail: string;
  teamLeaderRole: string;
  teamDownloadsCount: number;
  teamTotalSent: number;
  teamSize: number;
}

export interface GetHighestAppDownloadsByTeamResponse {
  status: string;
  message: string;
  data: IHighestAppDownloadsByTeam[];
}

/**
 * Get highest app downloads by team (Admin only)
 */
export const getHighestAppDownloadsByTeam = async (
  limit: number = 10
): Promise<IHighestAppDownloadsByTeam[]> => {
  try {
    const response = await axiosInstance.get<GetHighestAppDownloadsByTeamResponse>(
      `/nominations/highest-app-downloads-by-team?limit=${limit}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch highest app downloads by team:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch highest app downloads by team. Please try again.');
    throw error;
  }
};

/**
 * Interface for highest approaches by team result
 */
export interface IHighestApproachesByTeam {
  teamLeaderId: string;
  teamLeaderUsername: string;
  teamLeaderEmail: string;
  teamLeaderRole: string;
  teamApproachesCount: number;
  teamSize: number;
}

export interface GetHighestApproachesByTeamResponse {
  status: string;
  message: string;
  data: IHighestApproachesByTeam[];
}

/**
 * Get highest approaches by team (Admin only)
 */
export const getHighestApproachesByTeam = async (
  limit: number = 10
): Promise<IHighestApproachesByTeam[]> => {
  try {
    const response = await axiosInstance.get<GetHighestApproachesByTeamResponse>(
      `/nominations/highest-approaches-by-team?limit=${limit}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch highest approaches by team:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch highest approaches by team. Please try again.');
    throw error;
  }
};

/**
 * Interface for highest college referrals by team result
 */
export interface IHighestCollegeReferralsByTeam {
  teamLeaderId: string;
  teamLeaderUsername: string;
  teamLeaderEmail: string;
  teamLeaderRole: string;
  teamCollegeReferralCount: number;
  teamSize: number;
}

export interface GetHighestCollegeReferralsByTeamResponse {
  status: string;
  message: string;
  data: IHighestCollegeReferralsByTeam[];
}

/**
 * Get highest college referrals by team (Admin only)
 */
export const getHighestCollegeReferralsByTeam = async (
  limit: number = 10
): Promise<IHighestCollegeReferralsByTeam[]> => {
  try {
    const response = await axiosInstance.get<GetHighestCollegeReferralsByTeamResponse>(
      `/nominations/highest-college-referrals-by-team?limit=${limit}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch highest college referrals by team:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch highest college referrals by team. Please try again.');
    throw error;
  }
};

/**
 * Interface for highest company referrals by team result
 */
export interface IHighestCompanyReferralsByTeam {
  teamLeaderId: string;
  teamLeaderUsername: string;
  teamLeaderEmail: string;
  teamLeaderRole: string;
  teamCompanyReferralCount: number;
  teamSize: number;
}

export interface GetHighestCompanyReferralsByTeamResponse {
  status: string;
  message: string;
  data: IHighestCompanyReferralsByTeam[];
}

/**
 * Get highest company referrals by team (Admin only)
 */
export const getHighestCompanyReferralsByTeam = async (
  limit: number = 10
): Promise<IHighestCompanyReferralsByTeam[]> => {
  try {
    const response = await axiosInstance.get<GetHighestCompanyReferralsByTeamResponse>(
      `/nominations/highest-company-referrals-by-team?limit=${limit}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch highest company referrals by team:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch highest company referrals by team. Please try again.');
    throw error;
  }
};

/**
 * Interface for highest job posts created by team result
 */
export interface IHighestJobPostsCreatedByTeam {
  teamLeaderId: string;
  teamLeaderUsername: string;
  teamLeaderEmail: string;
  teamLeaderRole: string;
  teamJobPostsCount: number;
  teamSize: number;
}

export interface GetHighestJobPostsCreatedByTeamResponse {
  status: string;
  message: string;
  data: IHighestJobPostsCreatedByTeam[];
}

/**
 * Get highest job posts created by team (Admin only)
 */
export const getHighestJobPostsCreatedByTeam = async (
  limit: number = 10
): Promise<IHighestJobPostsCreatedByTeam[]> => {
  try {
    const response = await axiosInstance.get<GetHighestJobPostsCreatedByTeamResponse>(
      `/nominations/highest-job-posts-created-by-team?limit=${limit}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch highest job posts created by team:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch highest job posts created by team. Please try again.');
    throw error;
  }
};

/**
 * Interface for highest joinings by team result
 */
export interface IHighestJoiningsByTeam {
  teamLeaderId: string;
  teamLeaderUsername: string;
  teamLeaderEmail: string;
  teamLeaderRole: string;
  teamJoiningsCount: number;
  teamSize: number;
}

export interface GetHighestJoiningsByTeamResponse {
  status: string;
  message: string;
  data: IHighestJoiningsByTeam[];
}

/**
 * Get highest joinings by team (Admin only)
 */
export const getHighestJoiningsByTeam = async (
  limit: number = 10
): Promise<IHighestJoiningsByTeam[]> => {
  try {
    const response = await axiosInstance.get<GetHighestJoiningsByTeamResponse>(
      `/nominations/highest-joinings-by-team?limit=${limit}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch highest joinings by team:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch highest joinings by team. Please try again.');
    throw error;
  }
};

/**
 * Interface for highest selections by team result
 */
export interface IHighestSelectionsByTeam {
  teamLeaderId: string;
  teamLeaderUsername: string;
  teamLeaderEmail: string;
  teamLeaderRole: string;
  teamSelectionsCount: number;
  teamSize: number;
}

export interface GetHighestSelectionsByTeamResponse {
  status: string;
  message: string;
  data: IHighestSelectionsByTeam[];
}

/**
 * Get highest selections by team (Admin only)
 */
export const getHighestSelectionsByTeam = async (
  limit: number = 10
): Promise<IHighestSelectionsByTeam[]> => {
  try {
    const response = await axiosInstance.get<GetHighestSelectionsByTeamResponse>(
      `/nominations/highest-selections-by-team?limit=${limit}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch highest selections by team:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch highest selections by team. Please try again.');
    throw error;
  }
};

/**
 * Interface for highest interns added by team result
 */
export interface IHighestInternsAddedByTeam {
  teamLeaderId: string;
  teamLeaderUsername: string;
  teamLeaderEmail: string;
  teamLeaderRole: string;
  teamInternsAddedCount: number;
  teamSize: number;
}

export interface GetHighestInternsAddedByTeamResponse {
  status: string;
  message: string;
  data: IHighestInternsAddedByTeam[];
}

/**
 * Get highest interns added by team (Admin only)
 */
export const getHighestInternsAddedByTeam = async (
  limit: number = 10
): Promise<IHighestInternsAddedByTeam[]> => {
  try {
    const response = await axiosInstance.get<GetHighestInternsAddedByTeamResponse>(
      `/nominations/highest-interns-added-by-team?limit=${limit}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch highest interns added by team:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch highest interns added by team. Please try again.');
    throw error;
  }
};

/**
 * Interface for highest talent pool creation by team result
 */
export interface IHighestTalentPoolCreationByTeam {
  teamLeaderId: string;
  teamLeaderUsername: string;
  teamLeaderEmail: string;
  teamLeaderRole: string;
  teamTalentPoolCount: number;
  teamSize: number;
}

export interface GetHighestTalentPoolCreationByTeamResponse {
  status: string;
  message: string;
  data: IHighestTalentPoolCreationByTeam[];
}

/**
 * Get highest talent pool creation by team (Admin only)
 */
export const getHighestTalentPoolCreationByTeam = async (
  limit: number = 10
): Promise<IHighestTalentPoolCreationByTeam[]> => {
  try {
    const response = await axiosInstance.get<GetHighestTalentPoolCreationByTeamResponse>(
      `/nominations/highest-talent-pool-creation-by-team?limit=${limit}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch highest talent pool creation by team:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch highest talent pool creation by team. Please try again.');
    throw error;
  }
};

/**
 * Interface for highest walk-ins by team result
 */
export interface IHighestWalkInsByTeam {
  teamLeaderId: string;
  teamLeaderUsername: string;
  teamLeaderEmail: string;
  teamLeaderRole: string;
  teamWalkInsCount: number;
  teamSize: number;
}

export interface GetHighestWalkInsByTeamResponse {
  status: string;
  message: string;
  data: IHighestWalkInsByTeam[];
}

/**
 * Get highest walk-ins by team (Admin only)
 */
export const getHighestWalkInsByTeam = async (
  limit: number = 10
): Promise<IHighestWalkInsByTeam[]> => {
  try {
    const response = await axiosInstance.get<GetHighestWalkInsByTeamResponse>(
      `/nominations/highest-walk-ins-by-team?limit=${limit}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch highest walk-ins by team:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch highest walk-ins by team. Please try again.');
    throw error;
  }
};

/**
 * Interface for highest approach award result
 */
export interface IHighestApproachAward {
  userId: string;
  username: string;
  email: string;
  role?: string;
  approachesCount: number;
}

export interface GetHighestApproachAwardResponse {
  status: string;
  message: string;
  data: IHighestApproachAward[];
}

/**
 * Get highest approach award (Admin only)
 */
export const getHighestApproachAward = async (
  limit: number = 10
): Promise<IHighestApproachAward[]> => {
  try {
    const response = await axiosInstance.get<GetHighestApproachAwardResponse>(
      `/nominations/highest-approach-award?limit=${limit}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch highest approach award:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch highest approach award. Please try again.');
    throw error;
  }
};

/**
 * Interface for highest subjob post creation result
 */
export interface IHighestSubjobPostCreation {
  userId: string;
  username: string;
  email: string;
  role?: string;
  subjobsCount: number;
}

export interface GetHighestSubjobPostCreationResponse {
  status: string;
  message: string;
  data: IHighestSubjobPostCreation[];
}

/**
 * Get highest subjob post creation (Admin only)
 */
export const getHighestSubjobPostCreation = async (
  limit: number = 10
): Promise<IHighestSubjobPostCreation[]> => {
  try {
    const response = await axiosInstance.get<GetHighestSubjobPostCreationResponse>(
      `/nominations/highest-subjob-post-creation?limit=${limit}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch highest subjob post creation:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch highest subjob post creation. Please try again.');
    throw error;
  }
};

/**
 * Interface for highest joinings award result
 */
export interface IHighestJoiningsAward {
  userId: string;
  username: string;
  email: string;
  role?: string;
  joiningsCount: number;
}

export interface GetHighestJoiningsAwardResponse {
  status: string;
  message: string;
  data: IHighestJoiningsAward[];
}

/**
 * Get highest joinings award (Admin only)
 */
export const getHighestJoiningsAward = async (
  limit: number = 10
): Promise<IHighestJoiningsAward[]> => {
  try {
    const response = await axiosInstance.get<GetHighestJoiningsAwardResponse>(
      `/nominations/highest-joinings-award?limit=${limit}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch highest joinings award:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch highest joinings award. Please try again.');
    throw error;
  }
};

/**
 * Interface for highest selections award result
 */
export interface IHighestSelectionsAward {
  userId: string;
  username: string;
  email: string;
  role?: string;
  selectionsCount: number;
}

export interface GetHighestSelectionsAwardResponse {
  status: string;
  message: string;
  data: IHighestSelectionsAward[];
}

/**
 * Get highest selections award (Admin only)
 */
export const getHighestSelectionsAward = async (
  limit: number = 10
): Promise<IHighestSelectionsAward[]> => {
  try {
    const response = await axiosInstance.get<GetHighestSelectionsAwardResponse>(
      `/nominations/highest-selections-award?limit=${limit}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch highest selections award:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch highest selections award. Please try again.');
    throw error;
  }
};

/**
 * Interface for highest walk-ins award result
 */
export interface IHighestWalkInsAward {
  userId: string;
  username: string;
  email: string;
  role?: string;
  walkInsCount: number;
}

export interface GetHighestWalkInsAwardResponse {
  status: string;
  message: string;
  data: IHighestWalkInsAward[];
}

/**
 * Get highest walk-ins award (Admin only)
 * Note: "Walk-ins" refers to applications added by a user
 */
export const getHighestWalkInsAward = async (
  limit: number = 10
): Promise<IHighestWalkInsAward[]> => {
  try {
    const response = await axiosInstance.get<GetHighestWalkInsAwardResponse>(
      `/nominations/highest-walk-ins-award?limit=${limit}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch highest walk-ins award:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch highest walk-ins award. Please try again.');
    throw error;
  }
};

/**
 * Interface for highest talent pool creation award result
 */
export interface IHighestTalentPoolCreationAward {
  userId: string;
  username: string;
  email: string;
  role?: string;
  talentPoolCount: number;
}

export interface GetHighestTalentPoolCreationAwardResponse {
  status: string;
  message: string;
  data: IHighestTalentPoolCreationAward[];
}

/**
 * Get highest talent pool creation award (Admin only)
 */
export const getHighestTalentPoolCreationAward = async (
  limit: number = 10
): Promise<IHighestTalentPoolCreationAward[]> => {
  try {
    const response = await axiosInstance.get<GetHighestTalentPoolCreationAwardResponse>(
      `/nominations/highest-talent-pool-creation-award?limit=${limit}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch highest talent pool creation award:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch highest talent pool creation award. Please try again.');
    throw error;
  }
};

/**
 * Interface for highest college referral award result
 */
export interface IHighestCollegeReferralAward {
  userId: string;
  username: string;
  email: string;
  role?: string;
  collegeReferralCount: number;
}

export interface GetHighestCollegeReferralAwardResponse {
  status: string;
  message: string;
  data: IHighestCollegeReferralAward[];
}

/**
 * Get highest college referral award (Admin only)
 */
export const getHighestCollegeReferralAward = async (
  limit: number = 10
): Promise<IHighestCollegeReferralAward[]> => {
  try {
    const response = await axiosInstance.get<GetHighestCollegeReferralAwardResponse>(
      `/nominations/highest-college-referral-award?limit=${limit}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch highest college referral award:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch highest college referral award. Please try again.');
    throw error;
  }
};

/**
 * Interface for highest company referral award result
 */
export interface IHighestCompanyReferralAward {
  userId: string;
  username: string;
  email: string;
  role?: string;
  companyReferralCount: number;
}

export interface GetHighestCompanyReferralAwardResponse {
  status: string;
  message: string;
  data: IHighestCompanyReferralAward[];
}

/**
 * Get highest company referral award (Admin only)
 */
export const getHighestCompanyReferralAward = async (
  limit: number = 10
): Promise<IHighestCompanyReferralAward[]> => {
  try {
    const response = await axiosInstance.get<GetHighestCompanyReferralAwardResponse>(
      `/nominations/highest-company-referral-award?limit=${limit}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch highest company referral award:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to fetch highest company referral award. Please try again.');
    throw error;
  }
};

